import { FC, PropsWithChildren, ReactNode, useId } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { SMEPrice } from '@eneco/flows/components';
import { Box, Stack, Text, Badge, RadioCard, Divider, Stretch } from '@eneco/sparky';

import { Price as PriceType } from '../../types';

interface Props {
  id: string;
  title?: string;
  ribbon?: ReactNode;
  price?: PriceType;
  tag?: string;
  usps?: string[];
}

export const SMEFlowProductCard: FC<PropsWithChildren<Props>> = ({
  id: productId,
  title,
  ribbon,
  tag,
  price,
  usps,
  children,
}) => {
  const id = useId();

  const renderTag = () => {
    if (!tag) return null;

    return (
      <Stack.Item>
        <Badge color="alpha">{tag}</Badge>
      </Stack.Item>
    );
  };

  const renderUsps = () => {
    if (!usps?.length) return null;

    return (
      <Checklist>
        {usps.map(usp => (
          <Checklist.Item key={usp} gap="2">
            <RichText html={usp} />
          </Checklist.Item>
        ))}
      </Checklist>
    );
  };

  const renderPrice = () => {
    if (!price) return null;

    return (
      <Stack.Item shrink={{ initial: true, lg: false }}>
        <Box paddingTop="4">
          <Text>
            <SMEPrice id={`${id}-price`} suffix={price.period}>
              {price.value}
            </SMEPrice>
          </Text>
        </Box>
      </Stack.Item>
    );
  };

  return (
    <Stretch>
      <RadioCard value={productId} ariaLabelledby={`${id}-label ${id}-price`}>
        <Stretch>
          <Stack direction="column" gap="0">
            <Stack.Item grow={true}>
              {ribbon && (
                <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
                  {ribbon}
                </Box>
              )}

              <Box padding="6">
                <Stack gap="2">
                  <Stack direction="row" alignX="justify" gap="2">
                    <Stack direction="row" gap="2" alignY="start">
                      <Stack.Item shrink={false}>
                        <Box paddingTop="1">
                          <RadioCard.Indicator />
                        </Box>
                      </Stack.Item>

                      {title && (
                        <Text id={`${id}-label`} weight="bold">
                          {title}
                        </Text>
                      )}
                    </Stack>
                  </Stack>

                  {renderTag()}
                  {renderUsps()}
                  {renderPrice()}
                </Stack>
              </Box>
            </Stack.Item>

            {children && (
              <Stack.Item>
                <Divider />
                {children}
              </Stack.Item>
            )}
          </Stack>
        </Stretch>
      </RadioCard>
    </Stretch>
  );
};
